import React, { useEffect, useState, useContext, useRef, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

import api from "services/api";

import { Card } from "@mui/material";
import Grid from "@mui/material/Grid";

import Tooltip from "@mui/material/Tooltip";
import Pagination from "@mui/material/Pagination";
import CardMedia from "@mui/material/CardMedia";

import DataTable from "examples/Tables/DataTable";
import Actionables from "components/UpStyled/Actionables";

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import TableRowsIcon from "@mui/icons-material/TableRows";
import GridViewIcon from "@mui/icons-material/GridView";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import PageviewIcon from "@mui/icons-material/Pageview";
import EditIcon from "@mui/icons-material/Edit";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import { budgetOptions, MANUFACTURER_SERVICES } from "utils/data";
import SuiBadge from "components/SuiBadge";
import SuiTypography from "components/SuiTypography";
import SuiBadgeDot from "components/SuiBadgeDot";
import SuiBox from "components/SuiBox";
import SuiAvatar from "components/SuiAvatar";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import Switch from "@mui/material/Switch";

import { capitalCase } from "capital-case";
import { AccountContext } from "context/account";
import CategoryContext from "context/category-context";

import Prompt from "components/UpStyled/Prompt";
import toast from "react-hot-toast";
import moment from "moment";
import qs from "query-string";
import { useForm, Controller } from "react-hook-form";
import { DatePicker } from "antd";
import UTreeSelect from "components/UpStyled/UTreeSelect";

import { filterParser, sortParser } from "utils/helper";
import { stylesSortOptions, seasonOptions } from "utils/data";
import { handleUploadFiles, getStyleImages, canResizeImageUrl } from "utils/imageHelper";

import EmptySizeChartCard from "pages/de/size-chart/components/EmptySizeChartCard";
import CreateSizeChartModal from "pages/de/size-chart/components/CreateSizeChartModal";

import Error404 from "pages/error/404";

const SizeChart = ({ sharedView = false }) => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  const [createSizeChartVisible, setCreateSizeChartVisible] = useState(false);
  const [editSizeChartVisible, setEditSizeChartVisible] = useState(false);
  const [selectedSizeChart, setSelectedSizeChart] = useState({});

  const [deleteVisible, setDeleteVisible] = useState(false);

  const [loading, setLoading] = useState(false);
  const [isShowShared, setIsShowShared] = useState(false);

  let location = useLocation();
  const navigate = useNavigate();

  const user = useContext(AccountContext);

  const {
    tree,
    actions: { findNodeByID, findParents },
  } = useContext(CategoryContext);

  useEffect(() => {
    retrieveSizeCharts();
  }, []);

  const retrieveSizeCharts = async () => {
    try {
      const { data } = await api.sizeChart.list({
        order: "-updated_at",
      });
      let ownSizeCharts = data?.sizes || [];

      const shared = await api.sizeChart.sharedList({
        order: "-updated_at",
      });
      let sharedSizeCharts = shared?.data?.sizes || [];
      sharedSizeCharts = sharedSizeCharts.map((x) => ({ ...x, name: "Shared:" + x.name }));

      const allSizeCharts = [...ownSizeCharts, ...sharedSizeCharts];

      setData(allSizeCharts);
      setFilteredData(allSizeCharts);
    } catch (err) {
      toast.error(`Retrieve size chart list failed!`);
      console.log(err);
    }
  };

  const handleDeleteSizeChart = async (size) => {
    try {
      await api.sizeChart.delete(size.id);
      setDeleteVisible(false);
      retrieveSizeCharts();

      toast.success(`${size.name} has been successfully deleted!`);
    } catch (err) {
      toast.error(`${size.name} could not be deleted!`);
      console.log(err);
    }
  };

  const handleDuplicateSizeChart = async (data = {}) => {
    try {
      const payload = {
        name: "Duplicate of " + data["name"],
        sizes: data["sizes"],
        gender: data["gender"],
        category: data["category"],
        class: data["class"],
        attribute: data["attribute"],
        metadata: data["metadata"],
      };

      const newCreatedSizeChart = await api.sizeChart.create(payload);
      toast.success(`${payload["name"]} has been duplicated successfully!`);
      navigate(`/setup/size-master/${newCreatedSizeChart.data.result.id}`);
    } catch (err) {
      console.log(err);
      toast.error(`Size Chart could not be duplicated!`);
    }
  };

  const getStyleTableRow = (item) => {
    const categorys = item?.attribute?.category || [];
    let name = item?.name;
    if (item?.attribute?.unit?.value) {
      name = name + `(${item?.attribute?.unit?.label})`;
    }
    const isSharedWithMe = item.owner_id != user.id ? true : false;

    return {
      key: item.id,
      name: name,
      class: item?.class,
      gender: item?.gender,
      category: categorys.map((x) => x?.label).join(","),
      sizes: item?.sizes,
      created_at: item.created_at,
      updated_at: item.updated_at,
      isSharedWithMe: isSharedWithMe,
      action: item,
    };
  };

  const columns = [
    {
      Header: "Name",
      accessor: "name",
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word", maxWidth: 240 }}>{value}</div>;
      },
    },
    {
      Header: "Class",
      accessor: "class",
    },
    {
      Header: "Gender",
      accessor: "gender",
    },
    {
      Header: "Product Category",
      accessor: "category",
      Cell: ({ row, value }) => {
        return <div style={{ wordBreak: "break-word" }}>{value}</div>;
      },
    },
    {
      Header: "Sizes",
      accessor: "sizes",
      Cell: ({ row, value }) => {
        return (
          <div
            style={{
              wordBreak: "break-word",
              maxWidth: 180,
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      Header: "Last Edit",
      accessor: "updated_at",
      Cell: ({ row, value }) => {
        return value ? moment(value).format("MM/DD/YY") : "";
      },
    },
    {
      Header: "Ownership",
      accessor: "isSharedWithMe",
      Cell: ({ row, value }) => {
        return value ? "Admin" : "You";
      },
    },
    {
      Header: "More",
      accessor: "action",
      isSorted: false,
      canSort: false,
      Cell: ({ row, value }) => {
        const isSharedWithMe = row?.original?.isSharedWithMe;
        return (
          <SuiBox display="flex" alignItems="center" justifyContent="center" gap={0.5}>
            <Tooltip title="View Details">
              <SuiBox
                component={Link}
                to={
                  isSharedWithMe
                    ? `/shared-with-me/size-master/${value.id}`
                    : `/setup/size-master/${value.id}`
                }
              >
                <ManageSearchIcon fontSize={"small"} color="dark" />
              </SuiBox>
            </Tooltip>
            |
            {isSharedWithMe ? null : (
              <Tooltip title="Edit">
                <SuiBox>
                  <EditIcon
                    fontSize={"small"}
                    color="dark"
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedSizeChart(value);
                      setEditSizeChartVisible(true);
                    }}
                  />{" "}
                </SuiBox>
              </Tooltip>
            )}
            {isSharedWithMe ? null : "|"}
            {isSharedWithMe ? (
              <Tooltip title="Duplicate to my size charts">
                <SuiBox>
                  <ContentCopyIcon
                    fontSize={"small"}
                    color="dark"
                    sx={{ cursor: "pointer" }}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDuplicateSizeChart(value);
                    }}
                  />{" "}
                </SuiBox>
              </Tooltip>
            ) : (
              <Tooltip title="Delete">
                <SuiBox>
                  <DeleteForeverRoundedIcon
                    fontSize={"small"}
                    color="error"
                    sx={{ cursor: "pointer" }}
                    onClick={async (e) => {
                      e.stopPropagation();

                      setSelectedSizeChart(value);
                      setDeleteVisible(true);
                    }}
                  />{" "}
                </SuiBox>
              </Tooltip>
            )}
          </SuiBox>
        );
      },
    },
  ];

  const refresh = () => {
    retrieveSizeCharts();
  };

  const rows = filteredData.map((item) => getStyleTableRow(item));

  const handleRowClick = (row) => {
    if (row?.original?.isSharedWithMe) {
      navigate(`/shared-with-me/size-master/${row?.original?.key}`);
    } else {
      navigate(`/setup/size-master/${row?.original?.key}`);
    }
  };

  return (
    <SuiBox my={1}>
      <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={loading}>
        <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
      </Backdrop>
      <SuiBox display="flex" justifyContent="space-between" pb={1}>
        <SuiButton
          variant="gradient"
          color={"dark"}
          onClick={() => {
            setCreateSizeChartVisible(true);
          }}
        >
          New Size Chart
        </SuiButton>
      </SuiBox>

      <Card sx={{ p: 3 }}>
        <SuiBox>
          <SuiBox>
            <SuiTypography variant="body1" fontWeight="bold" display="inline" sx={{ mr: 1 }}>
              Show shared size charts
            </SuiTypography>
            <Switch
              defaultChecked={true}
              onChange={(e) => {
                const isCheckd = e?.target?.checked;
                if (isCheckd) {
                  setFilteredData(data);
                } else {
                  const filtered = data.filter((x) => x.owner_id == user.id);
                  setFilteredData(filtered);
                }
              }}
            />
          </SuiBox>
        </SuiBox>
        {rows.length > 0 ? (
          <DataTable
            table={{ columns, rows: rows }}
            entriesPerPage={{ defaultValue: 20 }}
            noEndBorder
            handleRowClick={handleRowClick}
          />
        ) : (
          <EmptySizeChartCard refresh={refresh} />
        )}
      </Card>
      {createSizeChartVisible ? (
        <CreateSizeChartModal
          handleClose={() => setCreateSizeChartVisible(false)}
          visible={createSizeChartVisible}
          isEdit={false}
          handleRefresh={refresh}
        />
      ) : null}
      {editSizeChartVisible && selectedSizeChart ? (
        <CreateSizeChartModal
          handleClose={() => setEditSizeChartVisible(false)}
          visible={editSizeChartVisible}
          isEdit={true}
          handleRefresh={refresh}
          sizeChart={selectedSizeChart}
        />
      ) : null}
      {deleteVisible && selectedSizeChart ? (
        <Prompt
          heading="Delete Size Chart"
          text={`Are you sure you want to delete the size chart: ${selectedSizeChart.name}`}
          visible={deleteVisible}
          okText="Delete"
          okColor="error"
          handleOk={() => handleDeleteSizeChart(selectedSizeChart)}
          handleClose={() => setDeleteVisible(false)}
        />
      ) : null}
    </SuiBox>
  );
};

export default SizeChart;
